import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bid, BidGroup, TimberItem } from '../../types/bidding';
import { useCRM } from '../../utils/CRMContext';
import { processDocument } from '../../utils/ocrService';
import { priceTimberItems } from '../../utils/googleSheetsService';
import { createDefaultGroup, recalculateGroup } from '../../utils/bidCalculator';
import { updateTimberItemBoardFeet } from '../../utils/timberListParser';
import BidGroupEditor from './BidGroupEditor';
import ManualTimberEntry from './ManualTimberEntry';
import TimberListEditor from './TimberListEditor';
import BidAssistant from './BidAssistant';
import { Bot, FileText, Pencil, AlertTriangle, Upload } from 'lucide-react';
import './BiddingModal.css';

interface BiddingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (bidId: string) => void;
  leadId?: string;
  dealId?: string;
  contactId?: string;
  initialProjectName?: string;
  // For editing existing bids
  existingBid?: Bid;
}

// Steps are reordered: Groups comes BEFORE Edit
type Step = 'create' | 'upload' | 'process' | 'groups' | 'edit' | 'configure';

interface StepInfo {
  id: Step;
  label: string;
  shortLabel: string;
}

const STEPS: StepInfo[] = [
  { id: 'create', label: 'Project Info', shortLabel: '1. Info' },
  { id: 'upload', label: 'Import Timber List', shortLabel: '2. Import' },
  { id: 'groups', label: 'Create Groups', shortLabel: '3. Groups' },
  { id: 'edit', label: 'Edit & Assign', shortLabel: '4. Timber' },
  { id: 'configure', label: 'Configure Costs', shortLabel: '5. Costs' },
];

// Chat state to preserve between navigation
interface ChatState {
  messages: any[];
  extractedItems: any[];
  uploadedImages: string[];
}

const BiddingModal: React.FC<BiddingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  leadId,
  dealId,
  contactId,
  initialProjectName = '',
  existingBid,
}) => {
  const { createBid, updateBid, generateBidNumber } = useCRM();
  
  const [step, setStep] = useState<Step>('create');
  const [projectName, setProjectName] = useState(initialProjectName);
  const [entryMode, setEntryMode] = useState<'upload' | 'manual' | 'assistant'>('assistant');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [ocrHints, setOcrHints] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [extractedItems, setExtractedItems] = useState<TimberItem[]>([]);
  const [groups, setGroups] = useState<BidGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [tempBidId, setTempBidId] = useState<string>('');
  
  // Preserve AI chat state
  const chatStateRef = useRef<ChatState | null>(null);
  
  // For editing existing bids
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalBidId, setOriginalBidId] = useState<string | null>(null);

  // Load existing bid data if provided
  useEffect(() => {
    if (existingBid) {
      setIsEditMode(true);
      setOriginalBidId(existingBid.id);
      setProjectName(existingBid.projectName);
      setGroups(existingBid.groups);
      
      // Collect all timber items from groups
      const allItems: TimberItem[] = [];
      existingBid.groups.forEach(group => {
        group.timberList.forEach(item => {
          allItems.push({ ...item, assignedToGroup: group.id });
        });
      });
      setExtractedItems(allItems);
      
      // Skip to configure step for existing bids
      setStep('configure');
    }
  }, [existingBid]);

  const resetForm = () => {
    setStep('create');
    setProjectName(initialProjectName);
    setEntryMode('assistant');
    setUploadedFiles([]);
    setOcrHints('');
    setProcessingStatus('');
    setProcessingProgress(0);
    setExtractedItems([]);
    setGroups([]);
    setError(null);
    setWarnings([]);
    setTempBidId('');
    setIsEditMode(false);
    setOriginalBidId(null);
    chatStateRef.current = null;
  };

  // Navigate to a step (with validation)
  const goToStep = (targetStep: Step) => {
    if (targetStep === 'process') return;
    
    if (targetStep === 'upload' && !projectName.trim()) {
      setError('Please enter a project name first');
      return;
    }
    if (targetStep === 'groups' && extractedItems.length === 0 && groups.length === 0) {
      // Allow going to groups to create groups first
    }
    if (targetStep === 'edit' && groups.length === 0) {
      setError('Please create at least one group first');
      return;
    }
    if (targetStep === 'configure' && groups.length === 0) {
      setError('Please create at least one group first');
      return;
    }
    
    setError(null);
    setStep(targetStep);
  };

  // Check if a step is accessible
  const isStepAccessible = (targetStep: Step): boolean => {
    if (targetStep === 'process') return false;
    if (targetStep === 'create') return true;
    if (targetStep === 'upload') return projectName.trim().length > 0;
    if (targetStep === 'groups') return projectName.trim().length > 0;
    if (targetStep === 'edit') return groups.length > 0;
    if (targetStep === 'configure') return groups.length > 0;
    return false;
  };

  // Get current step index
  const getCurrentStepIndex = (): number => {
    return STEPS.findIndex(s => s.id === step);
  };

  // Handle items extracted from AI assistant
  const handleAssistantExtractedItems = async (items: TimberItem[]) => {
    if (items.length === 0) return;
    
    setIsProcessing(true);
    const newWarnings: string[] = [];
    
    try {
      const itemsWithBoardFeet = items.map(item => updateTimberItemBoardFeet(item));
      
      try {
        const pricedItems = await priceTimberItems(itemsWithBoardFeet);
        const unpricedCount = pricedItems.filter(item => item.unitPrice === 0).length;
        if (unpricedCount > 0) {
          newWarnings.push(`${unpricedCount} items could not be priced automatically.`);
        }
        setExtractedItems(prev => [...prev, ...pricedItems]);
      } catch (err) {
        console.warn('Failed to fetch pricing:', err);
        newWarnings.push('Unable to fetch pricing. You can enter prices manually.');
        setExtractedItems(prev => [...prev, ...itemsWithBoardFeet]);
      }
      
      setWarnings(newWarnings);
    } finally {
      setIsProcessing(false);
    }
  };

  // Save chat state when navigating away from assistant
  const handleSaveChatState = useCallback((state: ChatState) => {
    chatStateRef.current = state;
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadedFiles(prevFiles => [...prevFiles, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(files => files.filter((_, i) => i !== index));
  };

  const handleProcessDocuments = async () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one document');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setStep('process');
    setProcessingProgress(0);

    const allExtractedItems: TimberItem[] = [];

    try {
      setProcessingStatus('Initializing...');
      setProcessingProgress(5);
      await generateBidNumber();
      const tempId = `temp-${Date.now()}`;
      setTempBidId(tempId);
      const totalFiles = uploadedFiles.length;

      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const fileNum = i + 1;
        
        setProcessingStatus(`Processing file ${fileNum} of ${totalFiles}: ${file.name}...`);
        setProcessingProgress(10 + (i / totalFiles) * 40);
        
        const result = await processDocument(tempId, file);
        allExtractedItems.push(...result.extractedItems);
        setProcessingProgress(10 + ((i + 1) / totalFiles) * 40);
      }

      setProcessingStatus(`Calculating board feet...`);
      setProcessingProgress(60);
      const itemsWithBoardFeet = allExtractedItems.map(item => updateTimberItemBoardFeet(item));

      let pricedItems = itemsWithBoardFeet;
      const newWarnings: string[] = [];
      
      try {
        setProcessingStatus('Fetching pricing...');
        setProcessingProgress(80);
        pricedItems = await priceTimberItems(itemsWithBoardFeet);
        const unpricedCount = pricedItems.filter(item => item.unitPrice === 0).length;
        if (unpricedCount > 0) {
          newWarnings.push(`${unpricedCount} items could not be priced automatically.`);
        }
      } catch (err) {
        newWarnings.push('Unable to fetch pricing. Enter prices manually.');
      }

      setProcessingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      setExtractedItems(pricedItems);
      setWarnings(newWarnings);
      setStep('groups'); // Go to groups step first
    } catch (err) {
      if (allExtractedItems.length > 0) {
        setExtractedItems(allExtractedItems);
        setStep('groups');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to process documents');
        setStep('upload');
      }
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
      setProcessingProgress(0);
    }
  };

  const handleProceedWithManualEntry = async () => {
    if (extractedItems.length === 0) {
      setError('Please add at least one timber item');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const pricedItems = await priceTimberItems(extractedItems);
      setExtractedItems(pricedItems);
      setStep('groups'); // Go to groups step
    } catch (err) {
      setStep('groups');
    } finally {
      setIsProcessing(false);
    }
  };

  // Create a new group
  const handleCreateGroup = (name: string) => {
    const newGroup = createDefaultGroup(name, groups.length);
    setGroups([...groups, newGroup]);
  };

  // Update group name
  const handleUpdateGroupName = (groupId: string, newName: string) => {
    setGroups(prevGroups => 
      prevGroups.map(g => g.id === groupId ? { ...g, name: newName } : g)
    );
  };

  // Delete a group (move items back to unassigned)
  const handleDeleteGroup = (groupId: string) => {
    // Unassign items from this group
    setExtractedItems(prev => 
      prev.map(item => item.assignedToGroup === groupId 
        ? { ...item, assignedToGroup: undefined } 
        : item
      )
    );
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  // Assign a timber item to a group
  const handleAssignItemToGroup = (itemId: string, groupId: string | undefined) => {
    setExtractedItems(prev => 
      prev.map(item => item.id === itemId 
        ? { ...item, assignedToGroup: groupId } 
        : item
      )
    );
  };

  // Build groups from timber items with assignments
  const buildGroupsFromAssignments = () => {
    const updatedGroups = groups.map(group => {
      const groupItems = extractedItems.filter(item => item.assignedToGroup === group.id);
      const timbersCost = groupItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const totalBoardFeet = groupItems.reduce((sum, item) => sum + (item.boardFeet * item.quantity), 0);
      
      const updatedGroup = {
        ...group,
        timberList: groupItems,
        materialsBucket: {
          ...group.materialsBucket,
          timbers: { items: groupItems, totalCost: timbersCost },
          total: timbersCost + group.materialsBucket.manualItems.reduce((s, i) => s + i.totalPrice, 0),
        },
        productionBucket: {
          ...group.productionBucket,
          totalBoardFeet,
          total: totalBoardFeet * group.productionBucket.ratePerBoardFoot,
        },
      };
      return recalculateGroup(updatedGroup);
    });
    
    setGroups(updatedGroups);
    setStep('configure');
  };

  const handleSaveBid = async () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (groups.length === 0) {
      setError('At least one group is required');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (isEditMode && originalBidId) {
        // Update existing bid
        await updateBid(originalBidId, {
          projectName: projectName.trim(),
          groups,
          updatedAt: new Date(),
        });
        resetForm();
        onClose();
        onSuccess?.(originalBidId);
      } else {
        // Create new bid
        const bidData: Omit<Bid, 'id' | 'createdAt' | 'updatedAt'> = {
          bidNumber: '',
          projectName: projectName.trim(),
          groups,
          sourceDocuments: [],
          status: 'draft',
          version: 1,
          createdBy: 'current-user',
          ...(leadId && { leadId }),
          ...(dealId && { dealId }),
          ...(contactId && { contactId }),
        };

        const bidId = await createBid(bidData);
        resetForm();
        onClose();
        onSuccess?.(bidId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save bid');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const currentStepIndex = getCurrentStepIndex();
  const unassignedItems = extractedItems.filter(item => !item.assignedToGroup);

  return (
    <div className="bidding-modal-overlay" onClick={handleClose}>
      <div className="bidding-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with Title */}
        <div className="modal-header">
          <h2>{isEditMode ? `Edit: ${projectName}` : (projectName || 'New Bid')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="breadcrumb-nav">
          {STEPS.map((stepInfo, index) => {
            const isActive = stepInfo.id === step;
            const isCompleted = index < currentStepIndex;
            const isAccessible = isStepAccessible(stepInfo.id);
            
            return (
              <React.Fragment key={stepInfo.id}>
                <button
                  className={`breadcrumb-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isAccessible ? 'disabled' : ''}`}
                  onClick={() => isAccessible && goToStep(stepInfo.id)}
                  disabled={!isAccessible}
                >
                  <span className="step-number">{index + 1}</span>
                  <span className="step-label">{stepInfo.label}</span>
                </button>
                {index < STEPS.length - 1 && (
                  <span className={`breadcrumb-arrow ${isCompleted ? 'completed' : ''}`}>→</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="modal-body">
          {/* STEP 1: Project Info */}
          {step === 'create' && (
            <div className="step-content">
              <div className="form-group">
                <label htmlFor="projectName">Project Name *</label>
                <input
                  id="projectName"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  autoFocus
                />
              </div>

              <div className="step-actions">
                <button className="cancel-btn" onClick={handleClose}>Cancel</button>
                <button
                  className="next-btn"
                  onClick={() => goToStep('upload')}
                  disabled={!projectName.trim()}
                >
                  Next: Import Timber List
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Import Timber List */}
          {step === 'upload' && (
            <div className="step-content">
              <div className="entry-mode-selector">
                <button
                  className={`mode-btn ${entryMode === 'assistant' ? 'active' : ''}`}
                  onClick={() => setEntryMode('assistant')}
                >
                  <Bot size={16} /> AI Assistant
                </button>
                <button
                  className={`mode-btn ${entryMode === 'upload' ? 'active' : ''}`}
                  onClick={() => setEntryMode('upload')}
                >
                  <FileText size={16} /> OCR Upload
                </button>
                <button
                  className={`mode-btn ${entryMode === 'manual' ? 'active' : ''}`}
                  onClick={() => setEntryMode('manual')}
                >
                  <Pencil size={16} /> Manual Entry
                </button>
              </div>

              {entryMode === 'assistant' && (
                <div className="assistant-container">
                  <BidAssistant
                    onExtractedItems={handleAssistantExtractedItems}
                    initialFiles={uploadedFiles}
                    savedState={chatStateRef.current}
                    onSaveState={handleSaveChatState}
                  />
                </div>
              )}

              {entryMode === 'upload' && (
                <>
                  <div className="upload-area">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".csv,.pdf,image/*"
                      multiple
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-upload" className="upload-label">
                      <div className="upload-icon"><Upload size={32} /></div>
                      <div className="upload-text"><strong>Click to upload</strong> or drag and drop</div>
                      <div className="upload-hint">CSV, PDF, or Images</div>
                    </label>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="uploaded-files">
                      <h4>Uploaded Files ({uploadedFiles.length})</h4>
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                          <button className="remove-file-btn" onClick={() => handleRemoveFile(index)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {entryMode === 'manual' && (
                <ManualTimberEntry items={extractedItems} onUpdate={setExtractedItems} />
              )}

              {extractedItems.length > 0 && (
                <div className="extracted-count-banner">
                  ✓ {extractedItems.length} timber items ready
                </div>
              )}

              <div className="step-actions">
                <button className="back-btn" onClick={() => goToStep('create')}>Back</button>
                {entryMode === 'upload' && (
                  <button
                    className="next-btn"
                    onClick={handleProcessDocuments}
                    disabled={uploadedFiles.length === 0 || isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Process Documents'}
                  </button>
                )}
                {entryMode === 'manual' && (
                  <button
                    className="next-btn"
                    onClick={handleProceedWithManualEntry}
                    disabled={extractedItems.length === 0 || isProcessing}
                  >
                    Continue
                  </button>
                )}
                <button
                  className="next-btn"
                  onClick={() => goToStep('groups')}
                >
                  {extractedItems.length > 0 
                    ? `Next: Create Groups (${extractedItems.length} items)` 
                    : 'Next: Create Groups'}
                </button>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {step === 'process' && (
            <div className="step-content processing-content">
              <div className="processing-animation"><div className="spinner"></div></div>
              <div className="processing-info">
                <h3>Processing Documents</h3>
                <p className="processing-status">{processingStatus}</p>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar-fill" style={{ width: `${processingProgress}%` }}>
                  <span className="progress-percentage">{Math.round(processingProgress)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Create Groups (BEFORE edit) */}
          {step === 'groups' && (
            <div className="step-content">
              <div className="groups-instructions">
                <p>Create groups to organize your bid (e.g., "Main Structure", "Front Porch", "Back Porch"). 
                   You'll assign timber items to these groups in the next step.</p>
              </div>

              {/* Create New Group */}
              <div className="create-group-section">
                <h4>Your Groups</h4>
                <div className="create-group-row">
                  <input
                    type="text"
                    id="new-group-name"
                    placeholder="Enter group name (e.g., Main Structure)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target as HTMLInputElement;
                        if (input.value.trim()) {
                          handleCreateGroup(input.value.trim());
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    className="create-group-btn"
                    onClick={() => {
                      const input = document.getElementById('new-group-name') as HTMLInputElement;
                      if (input.value.trim()) {
                        handleCreateGroup(input.value.trim());
                        input.value = '';
                      }
                    }}
                  >
                    + Add Group
                  </button>
                </div>
              </div>

              {/* List of Groups */}
              <div className="groups-list">
                {groups.length === 0 ? (
                  <div className="no-groups-message">
                    No groups yet. Create at least one group to continue.
                  </div>
                ) : (
                  groups.map(group => (
                    <div key={group.id} className="group-card">
                      <input
                        type="text"
                        value={group.name}
                        onChange={(e) => handleUpdateGroupName(group.id, e.target.value)}
                        className="group-name-input"
                      />
                      <span className="group-item-count">
                        {extractedItems.filter(i => i.assignedToGroup === group.id).length} items
                      </span>
                      <button 
                        className="delete-group-btn"
                        onClick={() => handleDeleteGroup(group.id)}
                        title="Delete group"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>

              {extractedItems.length > 0 && (
                <div className="items-summary">
                  <strong>{extractedItems.length}</strong> timber items ready to assign
                </div>
              )}

              <div className="step-actions">
                <button className="back-btn" onClick={() => goToStep('upload')}>Back</button>
                <button
                  className="next-btn"
                  onClick={() => goToStep('edit')}
                  disabled={groups.length === 0}
                >
                  Next: Edit & Assign Timber
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Edit & Assign Timber */}
          {step === 'edit' && (
            <div className="step-content">
              {warnings.length > 0 && (
                <div className="warnings-section">
                  {warnings.map((warning, index) => (
                    <div key={index} className="warning-message"><AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{warning}</div>
                  ))}
                </div>
              )}

              <div className="edit-instructions">
                <p>Edit timber details and assign each item to a group using the dropdown.</p>
              </div>

              {/* Timber List with Group Assignment */}
              <TimberListEditor
                items={extractedItems}
                onUpdate={setExtractedItems}
                readOnly={false}
                groups={groups}
                onAssignGroup={handleAssignItemToGroup}
              />

              {unassignedItems.length > 0 && (
                <div className="unassigned-warning">
                  <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{unassignedItems.length} item(s) not assigned to a group
                </div>
              )}

              <div className="step-actions">
                <button className="back-btn" onClick={() => goToStep('groups')}>Back</button>
                <button
                  className="next-btn"
                  onClick={buildGroupsFromAssignments}
                  disabled={groups.length === 0}
                >
                  Next: Configure Costs
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Configure Costs */}
          {step === 'configure' && (
            <div className="step-content">
              <BidGroupEditor
                groups={groups}
                onUpdate={setGroups}
                readOnly={false}
              />

              <div className="step-actions">
                <button className="back-btn" onClick={() => goToStep('edit')}>Back</button>
                <button className="save-btn" onClick={handleSaveBid} disabled={isProcessing}>
                  {isProcessing ? 'Saving...' : (isEditMode ? 'Update Bid' : 'Save Bid')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BiddingModal;
